import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, InitialPromptType, VoiceCallEndReason } from 'analytics/events';
import { ConfigurationSettings } from 'models/configuration-settings';
import { useVoiceCallStore } from 'state/store/chat/use-voice-call-store';
import { buildChatConversationPath } from 'utils/chat-utilities';
import {
  invalidateAgentConversationList,
  prependAgentConversationToCache,
} from '../utils/agent-sidebar-sync';
import {
  classifyVoiceSessionError,
  createVoiceSession,
  endVoiceSession,
  endVoiceSessionKeepalive,
} from '../services/voice-call-service';
import {
  connectVoiceRoom,
  disconnectVoiceRoom,
  isVoiceRoomActive,
  requestMicPermission,
  setVoiceMicEnabled,
} from '../services/voice-call-room';
import { VoiceSession } from '../types/voice-call.types';

interface StartVoiceCallOptions {
  agentId: string;
  /** Continue this conversation by voice; omit to start a fresh one. */
  conversationId?: string;
}

const MIC_ERROR_NAMES = ['NotAllowedError', 'NotFoundError', 'NotReadableError'];
/** The worker normally joins within seconds; past this we give up instead of hanging on "Connecting…". */
export const AGENT_JOIN_TIMEOUT_MS = 45_000;

const isMicError = (error: unknown): boolean =>
  error instanceof Error && MIC_ERROR_NAMES.includes(error.name);

const callDurationSeconds = (startedAt: number | null): number =>
  startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;

// Registered once per page: a tab closed mid-call still frees the room server-side.
let unloadConfig: ConfigurationSettings | null = null;
let unloadBound = false;
const bindPageHide = (config: ConfigurationSettings) => {
  unloadConfig = config;
  if (unloadBound || typeof window === 'undefined') return;
  unloadBound = true;
  window.addEventListener('pagehide', () => {
    const { roomName } = useVoiceCallStore.getState();
    if (!isVoiceRoomActive() || !roomName || !unloadConfig) return;
    void disconnectVoiceRoom();
    endVoiceSessionKeepalive(unloadConfig, roomName);
  });
};

/**
 * Orchestrates a voice call: mic pre-flight → `POST /voice/session` → (fresh chat: open the
 * new conversation) → join the LiveKit room. ChatPage skips its full-page spinner while a
 * call is active, so /chat → /chat/:id does not look like a reload.
 */
export const useVoiceCall = () => {
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    bindPageHide(configurationSettings);
  }, [configurationSettings]);

  const endCall = useCallback(
    async (reason: VoiceCallEndReason) => {
      const state = useVoiceCallStore.getState();
      if (state.status === 'idle') return;
      state.setStatus('ending');
      await disconnectVoiceRoom();
      if (state.roomName) {
        endVoiceSession(configurationSettings, state.roomName).catch(() => undefined);
      }
      trackEvent(
        EVENTS.VOICE_CALL_ENDED(state.conversationId, callDurationSeconds(state.startedAt), reason)
      );
      useVoiceCallStore.getState().reset();
    },
    [configurationSettings]
  );

  const hangUp = useCallback(() => endCall('user'), [endCall]);

  const startCall = useCallback(
    async ({ agentId, conversationId }: StartVoiceCallOptions) => {
      const store = useVoiceCallStore.getState();
      if (store.status !== 'idle') return;
      store.beginRequest(agentId);

      try {
        await requestMicPermission();
      } catch (error) {
        console.error('Microphone access denied or failed:', error);
        toast({
          variant: 'destructive',
          title: t('FAILED'),
          description: t('MICROPHONE_ACCESS_DENIED_OR_FAILED'),
        });
        useVoiceCallStore.getState().reset();
        return;
      }

      let session: VoiceSession;
      try {
        const response = await createVoiceSession(configurationSettings, {
          agentId,
          conversationId,
        });
        session = response.data;
      } catch (error) {
        const info = classifyVoiceSessionError(error);
        if (info.kind === 'unavailable') {
          useVoiceCallStore.getState().setUnavailable(true);
          toast({
            title: t('VOICE_CALL_UNAVAILABLE_TITLE', { defaultValue: 'Voice calls unavailable' }),
            description: t('VOICE_CALL_UNAVAILABLE', {
              defaultValue: "Voice calls aren't available here yet.",
            }),
          });
        } else {
          toast({
            variant: 'destructive',
            title: t('VOICE_CALL_FAILED', { defaultValue: 'Could not start the call' }),
            description:
              info.message ??
              t('VOICE_CALL_FAILED_GENERIC', {
                defaultValue: 'Something went wrong. Please try again.',
              }),
          });
        }
        useVoiceCallStore.getState().reset();
        return;
      }

      useVoiceCallStore
        .getState()
        .setSession({ conversationId: session.conversationId, roomName: session.room });
      trackEvent(EVENTS.VOICE_CALL_STARTED(session.conversationId, agentId));

      if (!conversationId) {
        // Fresh chat: the backend created the conversation without a WS event, so open it
        // now — the call's messages stream into it over the existing collaboration socket.
        trackEvent(EVENTS.NEW_CHAT_STARTED(session.conversationId, InitialPromptType.VOICE));
        prependAgentConversationToCache(
          queryClient,
          agentId,
          configurationSettings.blocksKey || '',
          session.conversationId
        );
        invalidateAgentConversationList(queryClient);
        navigate(buildChatConversationPath(session.conversationId, { agentId }));
      }

      let joinTimer: ReturnType<typeof setTimeout> | undefined;
      const clearJoinTimer = () => {
        if (joinTimer) clearTimeout(joinTimer);
        joinTimer = undefined;
      };
      try {
        await connectVoiceRoom(session, {
          onAgentJoined: () => {
            clearJoinTimer();
            useVoiceCallStore.getState().setStatus('in-call');
          },
          onAgentLeft: () => {
            console.warn('Voice call: the agent left the room');
            void endCall('agent_left');
          },
          onAgentState: (agentState) => useVoiceCallStore.getState().setAgentState(agentState),
          onCaption: (caption) => useVoiceCallStore.getState().upsertCaption(caption),
          onMediaDeviceError: (error) => {
            console.error('Voice call media device error:', error);
            toast({
              variant: 'destructive',
              title: t('FAILED'),
              description: t('MICROPHONE_ACCESS_DENIED_OR_FAILED'),
            });
          },
          onDisconnected: (reason) => {
            clearJoinTimer();
            const state = useVoiceCallStore.getState();
            if (state.status === 'idle' || state.status === 'ending') return;
            console.warn('Voice call: room disconnected', { reason, status: state.status });
            if (state.status === 'connecting') {
              toast({
                variant: 'destructive',
                title: t('VOICE_CALL_FAILED', { defaultValue: 'Could not start the call' }),
                description: t('VOICE_CALL_DISCONNECTED', {
                  defaultValue: 'The call was disconnected.',
                }),
              });
            }
            trackEvent(
              EVENTS.VOICE_CALL_ENDED(
                state.conversationId,
                callDurationSeconds(state.startedAt),
                'disconnected'
              )
            );
            state.reset();
          },
        });
        if (useVoiceCallStore.getState().status === 'connecting') {
          joinTimer = setTimeout(() => {
            if (useVoiceCallStore.getState().status !== 'connecting') return;
            toast({
              variant: 'destructive',
              title: t('VOICE_CALL_FAILED', { defaultValue: 'Could not start the call' }),
              description: t('VOICE_CALL_AGENT_NOT_JOINED', {
                defaultValue: 'The agent did not answer. Please try again.',
              }),
            });
            void endCall('error');
          }, AGENT_JOIN_TIMEOUT_MS);
        }
      } catch (error) {
        clearJoinTimer();
        console.error('Voice call connect failed:', error);
        toast({
          variant: 'destructive',
          title: t('FAILED'),
          description: isMicError(error)
            ? t('MICROPHONE_ACCESS_DENIED_OR_FAILED')
            : t('VOICE_CALL_FAILED_GENERIC', {
                defaultValue: 'Something went wrong. Please try again.',
              }),
        });
        await disconnectVoiceRoom();
        endVoiceSession(configurationSettings, session.room).catch(() => undefined);
        useVoiceCallStore.getState().reset();
      }
    },
    [configurationSettings, endCall, navigate, queryClient, t, toast]
  );

  const toggleMute = useCallback(async () => {
    const next = !useVoiceCallStore.getState().isMuted;
    await setVoiceMicEnabled(!next);
    useVoiceCallStore.getState().setMuted(next);
  }, []);

  return { startCall, hangUp, toggleMute };
};
